import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "../i18n";
import "./VisitorCheckIn.css";

interface VisitorCheckInProps {
  deviceId?: string;
  sessionToken?: string;
}

interface VisitorFormData {
  firstName: string;
  lastName: string;
  company: string;
  visitorWho: string;
}

const VisitorCheckIn: React.FC<VisitorCheckInProps> = ({
  deviceId,
  sessionToken,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get props from navigation state if available
  const stateProps = location.state as VisitorCheckInProps | undefined;
  const finalDeviceId = deviceId || stateProps?.deviceId;
  const finalSessionToken = sessionToken || stateProps?.sessionToken;

  const [formData, setFormData] = useState<VisitorFormData>({
    firstName: "",
    lastName: "",
    company: "",
    visitorWho: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<VisitorFormData>>({});
  const { t, translate } = useTranslation();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof VisitorFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<VisitorFormData> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t.visitorCheckIn.firstNameRequired;
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t.visitorCheckIn.lastNameRequired;
    }

    if (!formData.company.trim()) {
      newErrors.company = t.visitorCheckIn.companyRequired;
    }

    if (!formData.visitorWho.trim()) {
      newErrors.visitorWho = t.visitorCheckIn.whoToVisitRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // TODO: API call will be implemented later
      console.log("Visitor Check In Data:", {
        ...formData,
        deviceId: finalDeviceId,
        sessionToken: finalSessionToken,
        timestamp: new Date().toISOString(),
      });

      // For now, show success message and reset form
      alert(
        translate(t.visitorCheckIn.successMessage, {
          firstName: formData.firstName,
          lastName: formData.lastName,
        })
      );

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        company: "",
        visitorWho: "",
      });

      // Navigate back to home page
      navigate(-1);
    } catch (error) {
      console.error("Visitor check-in error:", error);
      alert(t.visitorCheckIn.errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="visitor-checkin-container">
      <div className="visitor-checkin-header">
        <button className="back-button" onClick={handleBack}>
          ← {t.visitorCheckIn.back}
        </button>
        <h1 className="visitor-checkin-title">{t.visitorCheckIn.title}</h1>
      </div>

      <div className="visitor-checkin-content">
        <form onSubmit={handleSubmit} className="visitor-form">
          <div className="form-group">
            <label htmlFor="firstName" className="form-label">
              {t.visitorCheckIn.firstName} {t.common.required}
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className={`form-input ${errors.firstName ? "error" : ""}`}
              placeholder={t.visitorCheckIn.firstNamePlaceholder}
              disabled={isLoading}
            />
            {errors.firstName && (
              <span className="error-message">{errors.firstName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="lastName" className="form-label">
              {t.visitorCheckIn.lastName} {t.common.required}
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className={`form-input ${errors.lastName ? "error" : ""}`}
              placeholder={t.visitorCheckIn.lastNamePlaceholder}
              disabled={isLoading}
            />
            {errors.lastName && (
              <span className="error-message">{errors.lastName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="company" className="form-label">
              {t.visitorCheckIn.company} {t.common.required}
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              className={`form-input ${errors.company ? "error" : ""}`}
              placeholder={t.visitorCheckIn.companyPlaceholder}
              disabled={isLoading}
            />
            {errors.company && (
              <span className="error-message">{errors.company}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="visitorWho" className="form-label">
              {t.visitorCheckIn.whoToVisit} {t.common.required}
            </label>
            <input
              type="text"
              id="visitorWho"
              name="visitorWho"
              value={formData.visitorWho}
              onChange={handleInputChange}
              className={`form-input ${errors.visitorWho ? "error" : ""}`}
              placeholder={t.visitorCheckIn.whoToVisitPlaceholder}
              disabled={isLoading}
            />
            {errors.visitorWho && (
              <span className="error-message">{errors.visitorWho}</span>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleBack}
              disabled={isLoading}
            >
              {t.visitorCheckIn.cancel}
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading
                ? t.visitorCheckIn.checkingIn
                : t.visitorCheckIn.checkIn}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VisitorCheckIn;
